/**
 * React hook for Google Auth state management.
 * Supports MCP OAuth (primary) and Firebase OAuth (legacy fallback).
 * MCP is preferred because tokens are managed server-side.
 */
import { useState, useEffect, useCallback } from 'react'
import {
  googleSignIn as firebaseGoogleSignIn,
  googleSignOut as firebaseGoogleSignOut,
  getGoogleToken,
  isGoogleConnected as isFirebaseConnected,
  getStoredGoogleUser,
  onGoogleAuthChange,
  checkRedirectResult,
  GoogleUser,
} from '../services/googleAuth'
import {
  isMcpConnected,
  getMcpGoogleUser,
  mcpGoogleSignIn,
  mcpGoogleSignOut,
  McpGoogleUser,
} from '../services/googleMcp'
interface UseGoogleAuthReturn {
  /** Whether the user is connected to Google (MCP or Firebase) */
  isConnected: boolean
  /** Whether auth is still initializing */
  isLoading: boolean
  /** Google user info (name, email, photo) */
  user: {
    uid?: string
    displayName: string | null
    email: string | null
    photoURL: string | null
  } | null
  /** Current access token (null if using MCP — tokens are server-side) */
  token: string | null
  /** Sign in with Google (MCP primary, Firebase fallback) */
  signIn: (email?: string) => Promise<void>
  /** Sign out of Google */
  signOut: () => Promise<void>
  /** Error message if sign-in failed */
  error: string | null
  /** Whether using MCP auth (vs Firebase) */
  isMcp: boolean
}
export function useGoogleAuth(): UseGoogleAuthReturn {
  const [isConnected, setIsConnected] = useState(
    () => isMcpConnected() || isFirebaseConnected(),
  )
  const [isLoading, setIsLoading] = useState(true)
  const [user, setUser] = useState<UseGoogleAuthReturn['user']>(() => {
    // Check MCP first, then Firebase
    const mcpUser = getMcpGoogleUser()
    if (mcpUser)
      return {
        displayName: mcpUser.displayName || null,
        email: mcpUser.email,
        photoURL: mcpUser.photoURL || null,
      }
    const fbUser = getStoredGoogleUser()
    if (fbUser) return fbUser
    return null
  })
  const [token, setToken] = useState(() => getGoogleToken())
  const [error, setError] = useState<string | null>(null)
  const [isMcp, setIsMcp] = useState(() => isMcpConnected())

  // On mount: check for pending redirect result + listen to auth state
  useEffect(() => {
    let mounted = true

    // If MCP is already connected, we're done
    if (isMcpConnected()) {
      setIsConnected(true)
      setIsMcp(true)
      setIsLoading(false)
      return
    }

    // Check if we're returning from a Firebase redirect sign-in
    checkRedirectResult()
      .then((googleUser) => {
        if (!mounted) return
        if (googleUser) {
          setUser({
            uid: googleUser.uid,
            displayName: googleUser.displayName,
            email: googleUser.email,
            photoURL: googleUser.photoURL,
          })
          setToken(googleUser.accessToken)
          setIsConnected(true)
          setIsMcp(false)
          setIsLoading(false)
        }
      })
      .catch(() => {})

    // Listen to Firebase auth state changes
    const unsubscribe = onGoogleAuthChange((firebaseUser) => {
      if (!mounted) return
      // Don't override MCP connection
      if (isMcpConnected()) {
        setIsLoading(false)
        return
      }
      if (firebaseUser) {
        const storedUser = getStoredGoogleUser()
        const storedToken = getGoogleToken()
        setUser(storedUser)
        setToken(storedToken)
        setIsConnected(!!storedToken)
        setIsMcp(false)
      } else {
        if (!isMcpConnected()) {
          setUser(null)
          setToken(null)
          setIsConnected(false)
        }
      }
      setIsLoading(false)
    })
    return () => {
      mounted = false
      unsubscribe()
    }
  }, [])

  // Periodically check token expiry (Firebase only — MCP tokens are server-side)
  useEffect(() => {
    if (!isConnected || isMcp) return
    const interval = setInterval(() => {
      const currentToken = getGoogleToken()
      if (!currentToken && isConnected && !isMcpConnected()) {
        setIsConnected(false)
        setToken(null)
        console.log(
          '[useGoogleAuth] Firebase token expired — user needs to re-authenticate',
        )
      }
    }, 60000)
    return () => clearInterval(interval)
  }, [isConnected, isMcp])
  const signIn = useCallback(async (email?: string) => {
    console.log('[useGoogleAuth] signIn called with email:', email || '(none)')
    setError(null)
    setIsLoading(true)
    try {
      // With email: try MCP first, fallback to Firebase
      if (email) {
        console.log('[useGoogleAuth] Attempting MCP sign-in for:', email)
        try {
          const mcpUser = await mcpGoogleSignIn(email)

          // MCP succeeded!
          console.log(
            '[useGoogleAuth] ✅ MCP sign-in succeeded for:',
            mcpUser.email,
          )
          setUser({
            displayName: mcpUser.displayName || null,
            email: mcpUser.email,
            photoURL: mcpUser.photoURL || null,
          })
          setToken(null)
          setIsConnected(true)
          setIsMcp(true)
          setIsLoading(false)
          return
        } catch (mcpErr: any) {
          console.log(
            '[useGoogleAuth] ❌ MCP failed, falling back to Firebase:',
            mcpErr.message,
          )
          // Fall through to Firebase
        }
      }

      // Firebase OAuth (fallback or no-email path)
      console.log('[useGoogleAuth] Starting Firebase sign-in...')
      const googleUser = await firebaseGoogleSignIn()
      console.log(
        '[useGoogleAuth] ✅ Firebase sign-in succeeded for:',
        googleUser.email,
      )
      setUser({
        uid: googleUser.uid,
        displayName: googleUser.displayName,
        email: googleUser.email,
        photoURL: googleUser.photoURL,
      })
      setToken(googleUser.accessToken)
      setIsConnected(true)
      setIsMcp(false)
    } catch (fbErr: any) {
      // Handle redirect (not an error)
      if (fbErr?.message?.includes('Redirigiendo')) {
        setError(null)
        return
      }
      // Handle user cancellation
      if (
        fbErr?.code === 'auth/popup-closed-by-user' ||
        fbErr?.code === 'auth/cancelled-popup-request'
      ) {
        setError(null)
      } else if (
        fbErr?.message?.includes('no está disponible en este entorno')
      ) {
        setError(fbErr.message)
      } else {
        setError(fbErr?.message || 'Error al conectar con Google')
      }
    } finally {
      setIsLoading(false)
    }
  }, [])
  const signOutFn = useCallback(async () => {
    try {
      if (isMcp) {
        mcpGoogleSignOut()
      } else {
        await firebaseGoogleSignOut()
      }
      setUser(null)
      setToken(null)
      setIsConnected(false)
      setIsMcp(false)
      setError(null)
    } catch (err: any) {
      console.error('[useGoogleAuth] Sign-out error:', err)
    }
  }, [isMcp])
  return {
    isConnected,
    isLoading,
    user,
    token,
    signIn,
    signOut: signOutFn,
    error,
    isMcp,
  }
}
